import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LiveHodoContext } from "@contexts/LiveHodoContext";
import moment from "moment";
import {
  selectIsLocalVideoEnabled,
  selectLocalPeer,
  selectLocalPeerID,
  selectVideoTrackByID,
  useAVToggle,
  useHMSStore,
  usePreviewJoin,
} from "@100mslive/react-sdk";
import { SettingsIcon } from "@100mslive/react-icons";
import {
  Avatar,
  Flex,
  flexCenter,
  Loading,
  styled,
  StyledVideoTile,
  Text,
  textEllipsis,
  Tooltip,
  useBorderAudioLevel,
  useTheme,
  Video,
} from "@100mslive/roomkit-react";
import { AudioVideoToggle } from "@components/AudioVideoToggle";
import TileConnection from "@components/Connection/TileConnection";
import FullPageProgress from "@components/FullPageProgress";
import PreviewName from "@components/Preview/PreviewName";
import SettingsModal from "@components/Settings/SettingsModal";
import { VirtualBackground } from "@plugins/VirtualBackground/VirtualBackground";
import { useUISettings } from "@components/AppData/useUISettings";
import {
  defaultPreviewPreference,
  UserPreferencesKeys,
  useUserPreferences,
} from "@components/hooks/useUserPreferences";
import { useGetUserData } from "@hooks/useGetUserData";
import { isMDT, isPMDT } from "@common/utils";
import request from "@services/request";
import { UI_SETTINGS } from "@common/constants";

import IconButton from "@/IconButton";
import { LANG_ARR_PRESENTATION } from "@/lang";
import {
  setAgendaAll,
  setRoomName,
  setSlideLanguage,
} from "@/reducers/counter";

const PreviewJoin = ({
  token,
  onJoin,
  env,
  skipPreview,
  initialName,
  asRole,
}) => {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const typeParam = params.get("type");
    if (typeParam) {
      const current = window.localStorage.getItem("type");
      if (!current || current !== typeParam) {
        window.localStorage.setItem("type", typeParam.toLowerCase());
      }
    }
  }, []);
  const {
    stateCtx: { channelRoom, mdtRoomDetail },
    createSocketConnection,
    setHasDoctorAI,
  } = useContext(LiveHodoContext);
  const localPeerId = useHMSStore(selectLocalPeerID);
  const { setCheckinMembers } = useContext(LiveHodoContext);
  const userData = useGetUserData();
  const [previewPreference, setPreviewPreference] = useUserPreferences(
    UserPreferencesKeys.PREVIEW,
    defaultPreviewPreference
  );

  const dispatch = useDispatch();

  const { t } = useTranslation();
  const [name, setName] = useState(initialName || previewPreference.name);
  const { isLocalAudioEnabled, isLocalVideoEnabled } = useAVToggle();
  const [previewError, setPreviewError] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const { enableJoin, preview, join } = usePreviewJoin({
    name,
    token,
    initEndpoint: env ? `https://${env}-init.100ms.live/init` : undefined,
    initialSettings: {
      isAudioMuted: skipPreview || previewPreference.isAudioMuted,
      isVideoMuted: skipPreview || previewPreference.isVideoMuted,
      speakerAutoSelectionBlacklist: ["Yeti Stereo Microphone"],
    },
    captureNetworkQualityInPreview: true,
    handleError: (_, method) => {
      if (method === "preview") {
        setPreviewError(true);
      }
    },
    asRole,
  });

  const checkIn = async params => {
    try {
      await request.checkIn(params);
    } catch (error) {
      console.log(error);
    }
  };

  const savePreferenceAndJoin = useCallback(async () => {
    setPreviewPreference({
      name,
      isAudioMuted: !isLocalAudioEnabled,
      isVideoMuted: !isLocalVideoEnabled,
    });
    if (isPMDT() || isMDT()) {
      try {
        await (isPMDT()
          ? request.PMdtCheckIn({
              public_mdt_id: mdtRoomDetail?.id,
              status: 1,
            })
          : checkIn({
              mdt_room_id: mdtRoomDetail?.id,
              date: moment().format("DD-MM-YYYY"),
              doctor_id: userData?.id,
              status: 1,
              reason_not_attend: null,
            }));
      } catch (checkinError) {
        console.error("Check-in failed:", checkinError);
        setPreviewError(true);
      }
      try {
        const params = {
          date: moment().format("YYYY-MM-DD"),
          mdt_room_id: mdtRoomDetail?.id,
          limit: 1000,
        };
        const resp = isPMDT()
          ? await request.getPMdtCheckinMembers(params)
          : await request.getMdtCheckinMembers(params);
        setCheckinMembers(resp?.data?.data || []);
      } catch (err) {
        console.log("Failed to fetch checkin members after join", err);
      }
    }
    await join();
    onJoin && onJoin();
    if (channelRoom) {
      channelRoom?.whisper("joinRoom", {
        id: userData.id,
        name: userData?.name,
        role: "doctor",
        peer_id: localPeerId,
      });
    }
  }, [
    join,
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    name,
    setPreviewPreference,
    onJoin,
    channelRoom,
  ]);
  useEffect(() => {
    if (token) {
      if (skipPreview) {
        savePreferenceAndJoin();
      } else {
        preview();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, skipPreview]);

  const getDiseases = async roomName => {
    try {
      const res = isPMDT()
        ? await request.getPMdtDiseases({
            public_mdt_id: roomName?.id,
            limit: 1000,
            sort: "created_at",
            sort_by: "asc",
            page: 1,
            date: new Date()
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-"),
          })
        : await request.getMdtDiseases({
            mdt_room_id: roomName?.id,
            limit: 1000,
            sort: "created_at",
            sort_by: "asc",
            page: 1,
            date: new Date()
              .toLocaleDateString("en-GB")
              .split("/")
              .reverse()
              .join("-"),
          });
      dispatch(setAgendaAll(res?.data?.data));
      res?.data?.data.forEach(agenda => {
        if (agenda.id && agenda.multilang) {
          dispatch(
            setSlideLanguage({
              slideId: agenda.id,
              lang: LANG_ARR_PRESENTATION.find(
                l => l.value === agenda.multilang[0].lang
              ),
            })
          );
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const getFeatures = async () => {
    try {
      const res1 = await request.getDoctorFeatures();
      const res2 = isPMDT()
        ? await request.getOrgFeatures(mdtRoomDetail?.organization_id)
        : await request.getWsFeatures(mdtRoomDetail?.clinic_id);
      setHasDoctorAI(res1?.data?.doctorAI || res2?.data?.doctorAI || false);
    } catch (error) {
      console.log(error);
    }
  };

  const showMdtRoom = async () => {
    try {
      const res = isPMDT()
        ? await request.showPMdtRoomByName(mdtRoomDetail?.room?.room_name)
        : await request.showMdtRoomByName(mdtRoomDetail?.room?.room_name);
      dispatch(setRoomName(res?.data));
      await getDiseases(res?.data);
      await getFeatures();
      createSocketConnection();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const preload = async () => {
      if (mdtRoomDetail?.room?.room_name) {
        setIsPreloading(true);
        await showMdtRoom();
        setIsPreloading(false);
      }
    };
    preload();
  }, [mdtRoomDetail]);
  if (isPreloading) {
    return <FullPageProgress />;
  }
  return (
    <Container>
      <Text
        variant="h4"
        css={{
          wordBreak: "break-word",
          textAlign: "center",
          color: "$on_surface_high",
        }}
      >
        {t("preview.get_started", "Get Started")}
      </Text>
      <Text
        css={{
          c: "$on_surface_medium",
          my: "$6",
          textAlign: "center",
          maxWidth: "100%",
          wordWrap: "break-word",
        }}
        variant="body1"
      >
        {t(
          "preview.setup_audio_before_joining",
          "Setup your audio and video before joining"
        )}
      </Text>
      <Flex
        align="center"
        justify="center"
        css={{
          "@sm": { width: "100%" },
          flexDirection: "column",
        }}
      >
        <PreviewTile name={name} error={previewError} />
        <PreviewControls
          enableJoin={enableJoin}
          savePreferenceAndJoin={savePreferenceAndJoin}
        />
        <PreviewName
          name={name}
          onChange={setName}
          enableJoin={enableJoin}
          onJoin={savePreferenceAndJoin}
        />
      </Flex>
    </Container>
  );
};

const Container = styled("div", {
  width: "100%",
  ...flexCenter,
  flexDirection: "column",
  px: "$10",
});

export const PreviewTile = ({ name, error }) => {
  const localPeer = useHMSStore(selectLocalPeer);
  const borderAudioRef = useBorderAudioLevel(localPeer?.audioTrack);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const mirrorLocalVideo = useUISettings(UI_SETTINGS.mirrorLocalVideo);
  const trackSelector = selectVideoTrackByID(localPeer?.videoTrack);
  const track = useHMSStore(trackSelector);

  const {
    aspectRatio: { width, height },
  } = useTheme();
  return (
    <StyledVideoTile.Container
      css={{
        bg: "$surface_default",
        aspectRatio: width / height,
        width: "unset",
        height: "min(360px, 60vh)",
        mt: "$12",
        "@sm": {
          height: "unset",
          width: "min(360px, 100%)",
          maxWidth: "100%",
        },
      }}
      ref={borderAudioRef}
    >
      {localPeer ? (
        <>
          <TileConnection name={name} peerId={localPeer.id} hideLabel={true} />
          <Video
            mirror={track?.facingMode !== "environment" && mirrorLocalVideo}
            trackId={localPeer.videoTrack}
            data-testid="preview_tile"
          />
          {!isVideoOn ? (
            <StyledVideoTile.AvatarContainer>
              <Avatar name={name} data-testid="preview_avatar_tile" />
              <Text css={{ ...textEllipsis("75%") }} variant="body2">
                {name}
              </Text>
            </StyledVideoTile.AvatarContainer>
          ) : null}
        </>
      ) : !error ? (
        <Loading size={100} />
      ) : null}
    </StyledVideoTile.Container>
  );
};

export const PreviewControls = () => {
  return (
    <Flex
      justify="between"
      css={{
        width: "100%",
        mt: "$8",
      }}
    >
      <Flex css={{ gap: "$4" }}>
        <AudioVideoToggle compact />
        <VirtualBackground />
      </Flex>
      <PreviewSettings />
    </Flex>
  );
};

const PreviewSettings = React.memo(() => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  return (
    <Fragment>
      <Tooltip title={t("lbl_setting", "Setting")}>
        <IconButton
          data-testid="preview_setting_btn"
          onClick={() => setOpen(value => !value)}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      {open && <SettingsModal open={open} onOpenChange={setOpen} />}
    </Fragment>
  );
});

export default PreviewJoin;
